import MessageHelper from '../../helpers/message-helper';

interface DataTablesSettingsWithButtons extends DataTables.Settings {
  buttons: any[]
}

const selectors = {
  container: '.js-datatable',
  selectAll: '.js-selectAll',
  rowCheckbox: '.js-rowSelect',
};

export default class Table {
  private readonly datatablesInstance: DataTables.Api;

  constructor(private container: JQuery, columnDefs?: DataTables.ColumnDefsSettings[], options?: Partial<DataTables.Settings>) {
    $.fn.dataTable.ext.classes.sLengthSelect = 'form-control';
    this.datatablesInstance = this.initLibrary(columnDefs, options);
    this.bindSelectAllClick();
  }

  static init(container: JQuery, columnDefs?: DataTables.ColumnDefsSettings[], options?: Partial<DataTables.Settings>): Table {
    return new Table(container, columnDefs, options);
  }

  static initAll(): Table[] {
    return $(selectors.container).map((index, datatable) => new Table($(datatable))).toArray();
  }

  static getInstance(settings: DataTables.SettingsLegacy): DataTables.Api {
    return new $.fn.dataTable.Api(settings);
  }

  private static getNumberFormat(): { decimal: string, thousands: string } {
    if (document.documentElement.lang === 'de') {
      return {
        decimal: ',',
        thousands: '.',
      };
    }
    return {
      decimal: '.',
      thousands: ',',
    };
  }

  getInstance(): DataTables.Api {
    return this.datatablesInstance;
  }

  setRows(data: unknown[]): void {
    this.datatablesInstance.clear();
    this.datatablesInstance.rows.add(data);
    this.datatablesInstance.draw();
    $('.js-totalRowCount').text(data.length ?? 0);
  }

  private initLibrary(columnDefs: DataTables.ColumnDefsSettings[] = [], options?: Partial<DataTables.Settings>): DataTables.Api {
    const pagination = this.container.data('pagination') !== false;

    let dom = 'r<"table-wrapper"t>';
    if (pagination) {
      dom += '<"table-controls mt-15 mb-0"<"table-controls__content-wrapper"<l>p>>';
    }
    if (this.container.data('hide-toolbar') !== true) {
      dom = `<"table-controls"f<"table-controls__content-wrapper js-dataTablesToolbar"B>>${dom}`;
    }

    let settings = {
      dom,
      searching: this.container.data('searchable') === true,
      info: false,
      paging: pagination,
      pagingType: 'numbers',
      lengthMenu: [
        [20, 40, 100],
        [
          `20 ${MessageHelper.getMessage('eparts.pagination.itemsPerPage')}`,
          `40 ${MessageHelper.getMessage('eparts.pagination.itemsPerPage')}`,
          `100 ${MessageHelper.getMessage('eparts.pagination.itemsPerPage')}`,
        ],
      ],
      columnDefs: [{
        targets: '-no-sort',
        orderable: false,
      }, {
        targets: '-searchable',
        searchable: true,
      },
      ...columnDefs,
      {
        targets: '_all',
        searchable: false,
      }],
      buttons: [],
      language: {
        lengthMenu: '_MENU_',
        emptyTable: '',
        search: '',
        searchPlaceholder: MessageHelper.getMessage('common.search'),
        ...Table.getNumberFormat(),
      },
      drawCallback(oSettings): void {
        // eslint-disable-next-line no-underscore-dangle
        if (oSettings.aoData.length <= oSettings._iDisplayLength) {
          $(oSettings.nTableWrapper)
            .find('.dataTables_paginate')
            .hide();
        } else {
          $(oSettings.nTableWrapper)
            .find('.dataTables_paginate')
            .show();
        }
      },
      initComplete() {
        const toolbarTarget = $('.js-dataTablesToolbarTarget');
        if (toolbarTarget.length) {
          const toolbar = $('.js-dataTablesToolbar');
          toolbar
            .children()
            .prependTo(toolbarTarget);

          const parent = toolbar.parent();
          toolbar.remove();
          if (parent.children().length === 0) {
            parent.remove();
          }
        }
      },
    } as DataTablesSettingsWithButtons;

    if (this.container.data('printable')) {
      settings.buttons.push({
        extend: 'print',
        exportOptions: {
          columns: ':not(.-hide-print)',
        },
        text: `${MessageHelper.getMessage('common.print')}`,
        title: '',
        customize(window: Window): void {
          $(window.document.body)
            .prepend($('.js-printAddition')
              .prop('outerHTML'))
            .prepend($('.js-additionalTableInfo')
              .prop('outerHTML'));
          $(window.document.body)
            .find('.js-printHeading')
            .removeClass('d-none');
        },
      });
    }

    if (options) {
      settings = {
        ...settings,
        ...options,
      };
    }

    const zeroRecordsTemplate = $('#js-dataTablesZeroRecordsTemplate');
    if (!this.isNestedTable() && zeroRecordsTemplate.length) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      settings.language!.zeroRecords = zeroRecordsTemplate.html();
    }

    if (this.hasSetOrdering()) {
      Object.assign(settings, this.orderSettings());
    }

    return this.container.DataTable(settings);
  }

  private hasSetOrdering(): boolean {
    return this.container.data('sorting-config') !== undefined;
  }

  private isNestedTable(): boolean {
    return this.container.parents('table').length > 0;
  }

  private orderSettings(): unknown {
    const order = (this.container.data('sorting-config') as string)
      .split(',')
      .map((el) => el.split(':'));
    return {
      order,
    };
  }

  private bindSelectAllClick(): void {
    this.container.find(selectors.selectAll)
      .on('change', (): void => {
        const allPages = this.datatablesInstance.rows()
          .nodes();
        $(allPages)
          .find(selectors.rowCheckbox)
          .prop('checked', $(selectors.selectAll)
            .is(':checked'))
          .trigger('change');
      });
  }
}
